import type { Edge, Node } from "reactflow";
import type { FunnelFlow, FunnelNodeData } from "@/funnel/types";

/**
 * Tiny DSL for authoring template flows by hand. Node ids are short stable
 * strings (unique within one template) — createMap keeps them verbatim,
 * which is fine because node ids only need uniqueness inside a map.
 */

type NodeOpts = {
  notes?: string;
  url?: string;
  props?: Record<string, string>;
  /** Parent flow/folder container id (position becomes relative). */
  parent?: string;
  w?: number;
  h?: number;
};

export function node(
  id: string,
  kind: string,
  label: string,
  x: number,
  y: number,
  opts: NodeOpts = {}
): Node<FunnelNodeData> {
  const data: FunnelNodeData = {
    kind,
    label,
    url: opts.url ?? "",
    notes: opts.notes ?? "",
    docs: [],
    props: opts.props ?? {},
  };
  return {
    id,
    type: kind === "util-note" ? "note" : "funnel",
    position: { x, y },
    data,
    ...(opts.parent ? { parentNode: opts.parent, extent: "parent" as const } : {}),
    ...(opts.w || opts.h ? { style: { width: opts.w, height: opts.h } } : {}),
  };
}

/** A Flow container. Children reference it via opts.parent. */
export function flow(
  id: string,
  label: string,
  x: number,
  y: number,
  w: number,
  h: number,
  opts: { notes?: string; status?: string } = {}
): Node<FunnelNodeData> {
  return {
    id,
    type: "flow",
    position: { x, y },
    data: {
      kind: "flow-container",
      label,
      url: "",
      notes: opts.notes ?? "",
      docs: [],
      props: opts.status ? { status: opts.status } : {},
    },
    style: { width: w, height: h },
  };
}

/** Horizontal edge (right → left handles). */
export function edge(source: string, target: string): Edge {
  return {
    id: `e-${source}-${target}`,
    source,
    target,
    sourceHandle: "sr",
    targetHandle: "tl",
  };
}

/** Vertical edge (bottom → top handles) — for steps stacked in a column. */
export function vedge(source: string, target: string): Edge {
  return {
    id: `e-${source}-${target}`,
    source,
    target,
    sourceHandle: "sb",
    targetHandle: "tt",
  };
}

export function makeFlow(nodes: Node<FunnelNodeData>[], edges: Edge[]): FunnelFlow {
  return { nodes, edges };
}

/** Stack step nodes in a column inside a flow container. */
export function column(
  parent: string,
  x: number,
  startY: number,
  gap: number,
  steps: [id: string, kind: string, label: string, opts?: NodeOpts][]
): { nodes: Node<FunnelNodeData>[]; edges: Edge[] } {
  const nodes = steps.map(([id, kind, label, opts], i) =>
    node(id, kind, label, x, startY + i * gap, { ...opts, parent })
  );
  const edges = steps
    .slice(1)
    .map(([id], i) => vedge(steps[i][0], id));
  return { nodes, edges };
}
